'use client';

import { type ComponentProps, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Tabs, Tag } from '@zillow/constellation-10';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { combinedAllowed, policiesForPet } from '@/src/lib/utils/petKind';
import { constellationTabBackgroundOverride } from '@/src/styles/constellationTabs.styles';
import { getSubGroupIcon } from '../icons';
import { getCollapsedLabel, groupAmenities, mergeUnitAndBuildingGroups } from '../utils/amenityGrouping';
import type { AmenityParentGroup, AmenitySubGroup, GroupedAmenity } from '../utils/amenityGrouping';
import {
  amenityItem,
  amenityList,
  leaseTermsText,
  policyList,
  policyRowInner,
  policyValue,
  spokeContentContainer,
  subGroupCard,
  subGroupIcon,
  subGroupsContainer,
  subGroupTitle,
} from './amenitiesSpoke.styles';

type HdpT = ReturnType<typeof useTranslations<'hdp'>>;

const DEFAULT_TAB_ORDER: Array<AmenityParentGroup> = ['Unit', 'Building', 'Policies'];

type PetPolicyData = {
  allowed: boolean | null;
  deposit: string | number | null;
  oneTimeFee: string | number | null;
  monthlyFee: string | number | null;
  maxNumberAllowed: string | number | null;
  maxWeightAllowed: string | number | null;
  comment: string;
  restriction: string;
};

type PetPoliciesMap = Record<string, Array<PetPolicyData> | undefined>;

type TagTone = ComponentProps<typeof Tag>['tone'];

export type PolicyRow = {
  label: string;
  value?: string;
  badgeTone?: TagTone;
  badgeText?: string;
};

// Some listings return numbers (e.g. maxNumberAllowed: 2) instead of strings.
// Coerce at the boundary so .match() doesn't crash.
const extractNumber = (s: string | number | null): string | null => {
  if (s == null) return null;
  const str = typeof s === 'string' ? s : String(s);
  const match = str.match(/^(\d+)/);
  return match ? match[1] : str;
};

export const buildPetPolicyRows = (petPolicies: PetPoliciesMap, t: HdpT): Array<PolicyRow> => {
  const dogs = policiesForPet<PetPolicyData>(petPolicies, 'dog');
  const cats = policiesForPet<PetPolicyData>(petPolicies, 'cat');

  if (!dogs.length && !cats.length) return [];

  const rows: Array<PolicyRow> = [];

  const dogAllowed = combinedAllowed(dogs);
  const catAllowed = combinedAllowed(cats);

  const allowedBadge = (allowed: boolean | null): Pick<PolicyRow, 'badgeTone' | 'badgeText'> =>
    allowed === null
      ? {}
      : { badgeTone: allowed ? 'teal' : 'critical', badgeText: allowed ? t('allowed') : t('notAllowed') };

  if (dogs.length && cats.length) {
    if (dogAllowed === catAllowed) {
      rows.push({ label: t('dogsAndCats'), ...allowedBadge(dogAllowed) });
    } else {
      rows.push({ label: t('dogs'), ...allowedBadge(dogAllowed) });
      rows.push({ label: t('cats'), ...allowedBadge(catAllowed) });
    }
  } else if (dogs.length) {
    rows.push({ label: t('dogs'), ...allowedBadge(dogAllowed) });
  } else if (cats.length) {
    rows.push({ label: t('cats'), ...allowedBadge(catAllowed) });
  }

  // Counts/fees from disallowed sizes don't apply; read allowed policies only (all when none allow).
  const allowedPolicies = [...dogs, ...cats].filter((policy) => policy.allowed);
  const detailSource = allowedPolicies.length ? allowedPolicies : [...dogs, ...cats];

  const extracted = detailSource
    .map((policy) => extractNumber(policy.maxNumberAllowed ?? null))
    .filter((value): value is string => Boolean(value));
  const numeric = extracted.map(Number).filter(Number.isFinite);
  const maxNum = numeric.length ? String(Math.max(...numeric)) : (extracted[0] ?? null);

  if (maxNum) {
    rows.push({ label: t('numberOfPetsAllowed'), value: maxNum });
  }

  const hasFees = detailSource.some((policy) => policy.deposit || policy.oneTimeFee || policy.monthlyFee);
  if (hasFees) {
    rows.push({ label: t('additionalPetFees') });
  }

  return rows;
};

const renderSubGroupCard = (sg: GroupedAmenity) => {
  const icon = getSubGroupIcon(sg.subGroup);
  return (
    <div key={sg.subGroup} className={subGroupCard}>
      <h3 className={subGroupTitle}>
        {icon && (
          <span className={subGroupIcon} aria-hidden="true">
            {icon}
          </span>
        )}
        {sg.subGroup}
      </h3>
      <ul className={amenityList}>
        {sg.items.map((item) => (
          <li key={item} className={amenityItem}>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Accepts the listing either as a legacy `config` prop or as a direct
 * `listing` prop from the inline HDP amenities module.
 */
type Props = {
  config?: unknown;
  listing?: PropertyDetailListing;
};

const tabLabel = (group: AmenityParentGroup, t: HdpT): string => {
  if (group === 'Unit') return t('tabUnit');
  if (group === 'Building') return t('tabBuilding');
  if (group === 'Policies') return t('tabPolicies');
  return group;
};

export const AmenitiesSpokeContent = ({ config, listing: listingProp }: Props) => {
  const t = useTranslations('hdp');
  const [activeTabGroup, setActiveTabGroup] = useState<AmenityParentGroup>('Unit');
  const listing = listingProp ?? (config as PropertyDetailListing | undefined) ?? null;

  const amenitiesData = listing?.amenities;
  const leaseTerms = listing?.details?.leaseTerms;
  const propertyType = (listing as unknown as { propertyType?: string } | null)?.propertyType;
  const listingType = (listing as unknown as { listingType?: string } | null)?.listingType;

  const rawAmenities = Array.isArray(amenitiesData?.amenities) ? amenitiesData.amenities : [];
  const petPolicies = (amenitiesData?.petPolicies ?? null) as PetPoliciesMap | null;
  // Note: parking highlights (types like "Covered", "Garage") belong under Building > Parking,
  // not Policies > Parking policy. They are intentionally not used here.
  const grouped = groupAmenities(rawAmenities);

  const collapsedLabel = getCollapsedLabel(propertyType, listingType);
  const displayGroups = collapsedLabel ? mergeUnitAndBuildingGroups(grouped, collapsedLabel) : grouped;
  const tabOrder: Array<AmenityParentGroup> = collapsedLabel ? [collapsedLabel, 'Policies'] : DEFAULT_TAB_ORDER;

  const hasPetPolicies = Boolean(petPolicies && (petPolicies.dog || petPolicies.cat || petPolicies.all));
  const hasStructuredPolicies = Boolean(leaseTerms || hasPetPolicies);

  const availableTabs = tabOrder.filter((pg) => {
    if (pg === 'Policies') {
      return displayGroups.some((g) => g.parentGroup === 'Policies') || hasStructuredPolicies;
    }
    return displayGroups.some((g) => g.parentGroup === pg);
  });

  const resolvedTab = availableTabs.includes(activeTabGroup) ? activeTabGroup : availableTabs[0];
  const activeGroup = displayGroups.find((g) => g.parentGroup === resolvedTab);

  if (availableTabs.length === 0) {
    return null;
  }

  const tabs = availableTabs.map((pg) => ({ id: pg, label: tabLabel(pg, t) }));

  const renderPoliciesContent = () => {
    const petPolicyRows = petPolicies ? buildPetPolicyRows(petPolicies, t) : [];

    // Only suppress string-based sub-groups when the structured data replacement exists
    const suppressedSubGroups: Array<AmenitySubGroup> = [];
    if (leaseTerms) suppressedSubGroups.push('Lease terms');
    if (hasPetPolicies) suppressedSubGroups.push('Pet policy');
    const remainingSubGroups = activeGroup?.subGroups.filter((sg) => !suppressedSubGroups.includes(sg.subGroup)) ?? [];

    return (
      <>
        {leaseTerms && (
          <div className={subGroupCard}>
            <h3 className={subGroupTitle}>
              <span className={subGroupIcon} aria-hidden="true">
                {getSubGroupIcon('Lease terms')}
              </span>
              {t('leaseTerms')}
            </h3>
            <p className={leaseTermsText}>{leaseTerms}</p>
          </div>
        )}

        {petPolicyRows.length > 0 && (
          <div className={subGroupCard}>
            <h3 className={subGroupTitle}>
              <span className={subGroupIcon} aria-hidden="true">
                {getSubGroupIcon('Pet policy')}
              </span>
              {t('petPolicy')}
            </h3>
            <ul className={policyList}>
              {petPolicyRows.map((row) => (
                <li key={row.label} className={amenityItem}>
                  <div className={policyRowInner}>
                    <span>{row.label}</span>
                    {row.badgeText && (
                      <Tag tone={row.badgeTone} size="sm">
                        {row.badgeText}
                      </Tag>
                    )}
                    {row.value && <span className={policyValue}>{row.value}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {remainingSubGroups.map(renderSubGroupCard)}
      </>
    );
  };

  return (
    <div className={spokeContentContainer}>
      <Tabs.Root
        selected={resolvedTab}
        onSelectedChange={(value) => setActiveTabGroup(value as AmenityParentGroup)}
        css={constellationTabBackgroundOverride}
      >
        <Tabs.List aria-label={t('amenityCategories')}>
          {tabs.map((tab) => (
            <Tabs.Tab key={tab.id} value={tab.id}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs.Root>

      <div
        role="tabpanel"
        aria-label={t('amenityTabPanel', { label: tabLabel(resolvedTab, t) })}
        className={subGroupsContainer}
      >
        {resolvedTab === 'Policies' ? renderPoliciesContent() : activeGroup?.subGroups.map(renderSubGroupCard)}
      </div>
    </div>
  );
};
